// :shortcode: → emoji expansion, Slack/Discord style.
//
// This is a curated set of the shortcodes people actually type in chat, not the
// full ~1800-entry Unicode/GitHub catalogue — that would bloat the bundle for
// codes nobody reaches for. Unknown shortcodes are left untouched as literal
// text (`:foo:` stays `:foo:`), which is the same thing Slack does.
//
// Expansion runs as a remark plugin (see `remarkEmoji`) so it only touches
// prose text nodes: a `:tada:` inside `inline code` or a ```fenced block```
// stays literal, which is what a user pasting a shortcode-as-example expects.

export const EMOJI_SHORTCODES: Record<string, string> = {
  // faces / reactions
  smile: '😄',
  smiley: '😃',
  grin: '😁',
  laughing: '😆',
  joy: '😂',
  rofl: '🤣',
  sweat_smile: '😅',
  wink: '😉',
  blush: '😊',
  slight_smile: '🙂',
  upside_down: '🙃',
  thinking: '🤔',
  neutral_face: '😐',
  expressionless: '😑',
  no_mouth: '😶',
  smirk: '😏',
  unamused: '😒',
  roll_eyes: '🙄',
  grimacing: '😬',
  relieved: '😌',
  pensive: '😔',
  confused: '😕',
  worried: '😟',
  cry: '😢',
  sob: '😭',
  weary: '😩',
  tired_face: '😫',
  triumph: '😤',
  angry: '😠',
  rage: '😡',
  exploding_head: '🤯',
  hot_face: '🥵',
  cold_face: '🥶',
  scream: '😱',
  fearful: '😨',
  flushed: '😳',
  sleeping: '😴',
  sunglasses: '😎',
  nerd: '🤓',
  star_struck: '🤩',
  partying_face: '🥳',
  heart_eyes: '😍',
  kissing_heart: '😘',
  yum: '😋',
  stuck_out_tongue: '😛',
  zany_face: '🤪',
  shushing_face: '🤫',
  face_with_monocle: '🧐',
  woozy_face: '🥴',
  poop: '💩',
  clown: '🤡',
  ghost: '👻',
  skull: '💀',
  alien: '👽',
  robot: '🤖',
  // hands / gestures
  '+1': '👍',
  thumbsup: '👍',
  '-1': '👎',
  thumbsdown: '👎',
  ok_hand: '👌',
  pinch: '🤏',
  v: '✌️',
  crossed_fingers: '🤞',
  love_you: '🤟',
  metal: '🤘',
  call_me: '🤙',
  point_up: '☝️',
  point_down: '👇',
  point_left: '👈',
  point_right: '👉',
  raised_hand: '✋',
  wave: '👋',
  raised_hands: '🙌',
  pray: '🙏',
  clap: '👏',
  handshake: '🤝',
  muscle: '💪',
  writing_hand: '✍️',
  fist: '✊',
  punch: '👊',
  // hearts / symbols
  heart: '❤️',
  orange_heart: '🧡',
  yellow_heart: '💛',
  green_heart: '💚',
  blue_heart: '💙',
  purple_heart: '💜',
  black_heart: '🖤',
  white_heart: '🤍',
  broken_heart: '💔',
  sparkling_heart: '💖',
  heartbeat: '💓',
  fire: '🔥',
  sparkles: '✨',
  star: '⭐',
  star2: '🌟',
  boom: '💥',
  zap: '⚡',
  100: '💯',
  tada: '🎉',
  confetti: '🎊',
  balloon: '🎈',
  gift: '🎁',
  trophy: '🏆',
  medal: '🏅',
  crown: '👑',
  gem: '💎',
  // status / work
  check: '✅',
  white_check_mark: '✅',
  heavy_check_mark: '✔️',
  x: '❌',
  negative_squared_cross_mark: '❎',
  warning: '⚠️',
  no_entry: '⛔',
  no_entry_sign: '🚫',
  question: '❓',
  exclamation: '❗',
  bangbang: '‼️',
  bulb: '💡',
  rocket: '🚀',
  hourglass: '⏳',
  alarm_clock: '⏰',
  watch: '⌚',
  calendar: '📅',
  date: '📆',
  pushpin: '📌',
  paperclip: '📎',
  link: '🔗',
  lock: '🔒',
  unlock: '🔓',
  key: '🔑',
  mag: '🔍',
  bell: '🔔',
  no_bell: '🔕',
  wrench: '🔧',
  hammer: '🔨',
  gear: '⚙️',
  bug: '🐛',
  package: '📦',
  memo: '📝',
  clipboard: '📋',
  chart_up: '📈',
  chart_down: '📉',
  moneybag: '💰',
  dollar: '💵',
  credit_card: '💳',
  email: '📧',
  envelope: '✉️',
  phone: '📱',
  computer: '💻',
  keyboard: '⌨️',
  printer: '🖨️',
  battery: '🔋',
  flashlight: '🔦',
  house: '🏠',
  office: '🏢',
  // nature / misc
  sunny: '☀️',
  cloud: '☁️',
  rain: '🌧️',
  snowflake: '❄️',
  zap_weather: '🌩️',
  rainbow: '🌈',
  moon: '🌙',
  earth: '🌍',
  ocean: '🌊',
  droplet: '💧',
  coffee: '☕',
  tea: '🍵',
  beer: '🍺',
  cake: '🍰',
  pizza: '🍕',
  hamburger: '🍔',
  apple: '🍎',
  dog: '🐶',
  cat: '🐱',
  eyes: '👀',
  brain: '🧠',
  wave_ok: '🆗',
  new: '🆕',
  free: '🆓',
  cool: '🆒',
  up: '🆙',
  soon: '🔜',
}

// Shortcode grammar: colon-delimited, letters/digits/underscore, plus the
// handful of punctuation codes (+1, -1) that live in the table above. Kept
// tight so ordinary prose like "the ratio is 3:4:5" or a `::` in code doesn't
// get chewed up.
const SHORTCODE_RE = /:([a-z0-9_+-]+):/g

/** Replace known `:shortcode:` tokens in a plain string; unknown ones are left
 *  literal. Exposed for the composer/unit tests; the render path uses the
 *  remark plugin below so code spans are skipped. */
export function replaceShortcodes(text: string): string {
  return text.replace(SHORTCODE_RE, (whole, name: string) => EMOJI_SHORTCODES[name] ?? whole)
}

// Minimal mdast node shape we care about — enough to walk children and rewrite
// text leaves without pulling in @types/mdast just for a tree walk.
type MdastNode = {
  type: string
  value?: string
  children?: MdastNode[]
}

/** remark plugin: expand `:shortcode:` tokens in text nodes only. Code blocks
 *  and inline code are leaf nodes we never recurse into, so a shortcode shown
 *  as an example inside code stays literal. */
export function remarkEmoji() {
  return (tree: MdastNode) => {
    walk(tree)
  }
}

function walk(node: MdastNode): void {
  const children = node.children
  if (!children) return
  for (const child of children) {
    if (child.type === 'text' && typeof child.value === 'string') {
      child.value = replaceShortcodes(child.value)
    } else if (child.type !== 'code' && child.type !== 'inlineCode') {
      walk(child)
    }
  }
}
